// Third-party
import React from 'react';

// Components
import { SelectInput, SelectOption } from '../../components/inputs';
import InfoTooltip from '../../components/tooltips/InfoTooltip';

// Utils
import { USER_PREFERENCES_COMPARISON_SETTINGS } from '@/redux/modules/user-preferences';
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { userHasPermission, PERMISSIONS } from '@/utils/permissions';
import User from '@/types/user';
import UserPreferencesComparisonSettings from '@/types/user-preferences-comparison-settings';
import { TIME_PERIODS } from '@/utils/constants';
import { filteredPerformanceAndRiskDataOptions } from '@/utils/preferences';

type Props = {
  user: User;
  comparisonSettings: UserPreferencesComparisonSettings;
  onComparisonSettingsChange: (option: { [key: string]: string }) => void;
};

const LegacyComparisonSettingsOptions = ({ user, comparisonSettings, onComparisonSettingsChange }: Props) => (
  <div className="preferences" data-testid="legacy-preferences">
    <div className="preference">
      <FormattedMessage tagName="h3" id="Settings.Preferences.ComparisonSettings.Title" />
      <FormattedMessage tagName="p" id="Settings.Preferences.ComparisonSettings.Description" />
    </div>
    <div className="options">
      {userHasPermission(user, PERMISSIONS.BENCHMARKS) && userHasPermission(user, PERMISSIONS.COMPARISON_AUTO_BENCHMARK) && (
        <SelectInput
          label={formatMessage('Settings.Preferences.ComparisonSettings.Fields.InitialBenchmark.Label')}
          onValueChange={value => onComparisonSettingsChange({ initialBenchmark: value })}
          selected={comparisonSettings.initialBenchmark}
          tooltip={
            <InfoTooltip
              title={formatMessage('Settings.Preferences.ComparisonSettings.Fields.InitialBenchmark.Label')}
              message={formatMessage('Settings.Preferences.ComparisonSettings.Fields.InitialBenchmark.Disclaimer')}
              data-private
              data-dd-privacy="mask"
            />
          }
        >
          {USER_PREFERENCES_COMPARISON_SETTINGS.INITIAL_BENCHMARK.map(option => (
            <SelectOption key={option.value} value={option.value}>
              {formatMessage(option.i18nKey)}
            </SelectOption>
          ))}
        </SelectInput>
      )}

      {userHasPermission(user, PERMISSIONS.MONTH_END_DATA) && (
        <SelectInput
          label={formatMessage('Settings.Preferences.ComparisonSettings.Fields.PerformanceAndRiskData.Label')}
          onValueChange={value => onComparisonSettingsChange({ performanceAndRiskData: value })}
          selected={comparisonSettings.performanceAndRiskData}
          tooltip={
            <InfoTooltip
              title={formatMessage('Settings.Preferences.ComparisonSettings.Fields.PerformanceAndRiskData.Label')}
              message={formatMessage('Settings.Preferences.ComparisonSettings.Fields.PerformanceAndRiskData.Disclaimer')}
              data-private
              data-dd-privacy="mask"
            />
          }
        >
          {filteredPerformanceAndRiskDataOptions(user, true).map(option => (
            <SelectOption key={option.value} value={option.value}>
              {formatMessage(option.i18nKey)}
            </SelectOption>
          ))}
        </SelectInput>
      )}

      <SelectInput
        label={formatMessage('Settings.Preferences.ComparisonSettings.Fields.HypotheticalReturnChartDetail.Label')}
        onValueChange={value => onComparisonSettingsChange({ hypotheticalReturnChartDetail: value })}
        selected={comparisonSettings.hypotheticalReturnChartDetail}
        tooltip={
          <InfoTooltip
            title={formatMessage('Settings.Preferences.ComparisonSettings.Fields.HypotheticalReturnChartDetail.Label')}
            message={formatMessage('Settings.Preferences.ComparisonSettings.Fields.HypotheticalReturnChartDetail.Disclaimer')}
            data-private
            data-dd-privacy="mask"
          />
        }
      >
        {USER_PREFERENCES_COMPARISON_SETTINGS.HYPOTHETICAL_RETURN_CHART_DETAIL.map(option => (
          <SelectOption key={option.value} value={option.value}>
            {formatMessage(option.i18nKey)}
          </SelectOption>
        ))}
      </SelectInput>

      <SelectInput
        label={formatMessage('Settings.Preferences.ComparisonSettings.Fields.HypotheticalReturnChartHorizon.Label')}
        onValueChange={value => onComparisonSettingsChange({ hypotheticalReturnChartHorizon: value })}
        selected={comparisonSettings.hypotheticalReturnChartHorizon}
        tooltip={
          <InfoTooltip
            title={formatMessage('Settings.Preferences.ComparisonSettings.Fields.HypotheticalReturnChartHorizon.Label')}
            message={formatMessage('Settings.Preferences.ComparisonSettings.Fields.HypotheticalReturnChartHorizon.Disclaimer')}
            data-private
            data-dd-privacy="mask"
          />
        }
      >
        {USER_PREFERENCES_COMPARISON_SETTINGS.HYPOTHETICAL_RETURN_CHART_HORIZON.map(option => (
          <SelectOption key={option.value} value={option.value}>
            {userHasPermission(user, PERMISSIONS.COMMON_INCEPTION) && option.value === TIME_PERIODS.CI
              ? formatMessage('Settings.Preferences.ComparisonSettings.Fields.HypotheticalReturnChartHorizon.Options.CI')
              : formatMessage(option.i18nKey)}
          </SelectOption>
        ))}
      </SelectInput>

      {userHasPermission(user, PERMISSIONS.CURRENCY_CONVERSIONS) && (
        <SelectInput
          label={formatMessage('Settings.Preferences.ComparisonSettings.Fields.BaseCurrency.Label')}
          onValueChange={value => onComparisonSettingsChange({ baseCurrency: value })}
          selected={comparisonSettings.baseCurrency}
          tooltip={
            <InfoTooltip
              title={formatMessage('Settings.Preferences.ComparisonSettings.Fields.BaseCurrency.Label')}
              message={formatMessage('Settings.Preferences.ComparisonSettings.Fields.BaseCurrency.Disclaimer')}
              data-private
              data-dd-privacy="mask"
            />
          }
        >
          <SelectOption key="Yes" value>
            {formatMessage('Settings.Preferences.ComparisonSettings.Fields.BaseCurrency.Options.Yes')}
          </SelectOption>
          <SelectOption key="No" value={false}>
            {formatMessage('Settings.Preferences.ComparisonSettings.Fields.BaseCurrency.Options.No')}
          </SelectOption>
        </SelectInput>
      )}
    </div>
  </div>
);

export default LegacyComparisonSettingsOptions;
